import React, { useEffect, useState, useMemo } from "react";
import {
  X,
  ShieldAlert,
  User,
  Activity,
  FileText,
  Trash2,
  RotateCcw,
  Plus,
  AlertTriangle,
  ArrowRight,
  Calendar,
  Filter,
} from "lucide-react";
import { getAuditLogs, AuditLogEntry } from "../services/auditService";

interface AuditLogPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function AuditLogPanel({ isOpen, onClose }: AuditLogPanelProps) {
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<string>("all");
  const [selectedDate, setSelectedDate] = useState<string>("");

  useEffect(() => {
    if (isOpen) {
      loadLogs();
    }
  }, [isOpen]);

  const loadLogs = async () => {
    setLoading(true);
    // Fetch more logs to allow better client-side filtering
    const data = await getAuditLogs(200);
    setLogs(data);
    setLoading(false);
  };

  const users = useMemo(() => {
    const u = new Set<string>();
    logs.forEach((log) => {
      if (log.actor?.email) u.add(log.actor.email);
    });
    return Array.from(u).sort();
  }, [logs]);

  const filteredLogs = useMemo(() => {
    return logs.filter((log) => {
      if (selectedUser !== "all" && log.actor?.email !== selectedUser)
        return false;
      if (selectedDate) {
        const logDate = new Date(log.timestamp.seconds * 1000)
          .toISOString()
          .split("T")[0];
        if (logDate !== selectedDate) return false;
      }
      return true;
    });
  }, [logs, selectedUser, selectedDate]);

  const groupedLogs = useMemo(() => {
    if (filteredLogs.length === 0) return [];

    const grouped: AuditLogEntry[] = [];
    let currentGroup: AuditLogEntry | null = null;

    filteredLogs.forEach((log) => {
      if (!currentGroup) {
        currentGroup = { ...log, diff: log.diff ? [...log.diff] : [] };
        return;
      }

      // Check criteria for grouping
      const sameUser = currentGroup.actor.email === log.actor.email;
      const sameTarget = currentGroup.target.id === log.target.id;
      const sameAction = currentGroup.action === log.action;

      const timeDiff =
        Math.abs(currentGroup.timestamp.seconds - log.timestamp.seconds) / 60; // in minutes

      const isWithinTimeWindow = timeDiff < 5; // 5 minute window

      if (sameUser && sameTarget && sameAction && isWithinTimeWindow) {
        // Merge diffs
        if (log.diff) {
          currentGroup.diff = [...(currentGroup.diff || []), ...log.diff];
        }
        // Keep the latest timestamp (since we iterate desc, the first one encountered is the latest,
        // but if we want to show the range or just the "session" time, keeping the first one (latest) is fine.
        // Actually, logs are likely sorted desc. So currentGroup is newer than log.
      } else {
        grouped.push(currentGroup);
        currentGroup = { ...log, diff: log.diff ? [...log.diff] : [] };
      }
    });

    if (currentGroup) {
      grouped.push(currentGroup);
    }

    return grouped;
  }, [filteredLogs]);

  const formatTime = (timestamp: any) => {
    if (!timestamp) return "";
    const date = new Date(timestamp.seconds * 1000);
    return date.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getActionIcon = (action: string) => {
    switch (action) {
      case "CREATE":
        return <Plus size={14} className="text-green-600" />;
      case "DELETE":
        return <Trash2 size={14} className="text-red-600" />;
      case "RESTORE":
        return <RotateCcw size={14} className="text-teal-600" />;
      case "STATUS_CHANGE":
        return <Activity size={14} className="text-blue-600" />;
      case "UPDATE":
        return <FileText size={14} className="text-orange-600" />;
      default:
        return <AlertTriangle size={14} className="text-gray-400" />;
    }
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case "CREATE":
        return "bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800";
      case "DELETE":
        return "bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800";
      case "RESTORE":
        return "bg-teal-50 border-teal-200 dark:bg-teal-900/20 dark:border-teal-800";
      case "STATUS_CHANGE":
        return "bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800";
      case "UPDATE":
        return "bg-orange-50 border-orange-200 dark:bg-orange-900/20 dark:border-orange-800";
      default:
        return "bg-gray-50 border-gray-200 dark:bg-slate-800 dark:border-slate-700";
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-y-0 right-0 w-full md:w-[480px] bg-white dark:bg-slate-900 shadow-2xl z-[70] border-l border-gray-200 dark:border-slate-700 flex flex-col transform transition-transform duration-300 ease-in-out">
      {/* Header */}
      <div className="p-4 border-b border-gray-100 dark:border-slate-800 flex justify-between items-center bg-gray-50 dark:bg-slate-900">
        <h2 className="font-bold text-slate-800 dark:text-white flex items-center gap-2">
          <ShieldAlert size={18} className="text-slate-500" /> Global Audit Log
        </h2>
        <button
          onClick={onClose}
          className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-full hover:bg-gray-200 dark:hover:bg-slate-800 transition-colors"
        >
          <X size={18} />
        </button>
      </div>

      {/* Filters */}
      <div className="p-3 border-b border-gray-100 dark:border-slate-800 bg-white dark:bg-slate-900 flex gap-2 overflow-x-auto">
        <div className="relative flex-1 min-w-[140px]">
          <User size={14} className="absolute left-2.5 top-2.5 text-gray-400" />
          <select
            value={selectedUser}
            onChange={(e) => setSelectedUser(e.target.value)}
            className="w-full pl-8 pr-2 py-2 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-xs appearance-none outline-none focus:ring-2 focus:ring-blue-500 dark:text-white cursor-pointer"
          >
            <option value="all">All Users</option>
            {users.map((u) => (
              <option key={u} value={u}>
                {u.split("@")[0]}
              </option>
            ))}
          </select>
        </div>
        <div className="relative flex-1 min-w-[140px]">
          <Calendar
            size={14}
            className="absolute left-2.5 top-2.5 text-gray-400"
          />
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="w-full pl-8 pr-2 py-2 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-xs outline-none focus:ring-2 focus:ring-blue-500 dark:text-white"
          />
        </div>
        {(selectedUser !== "all" || selectedDate) && (
          <button
            onClick={() => {
              setSelectedUser("all");
              setSelectedDate("");
            }}
            className="p-2 text-red-500 bg-red-50 dark:bg-red-900/20 rounded-lg hover:bg-red-100 transition-colors"
            title="Clear Filters"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-white dark:bg-slate-900">
        {loading && (
          <div className="text-center py-8 text-gray-400 text-sm animate-pulse">
            Loading audit trail...
          </div>
        )}

        {!loading && groupedLogs.length === 0 && (
          <div className="text-center text-gray-400 py-12 text-sm">
            <div className="mx-auto w-12 h-12 bg-gray-100 dark:bg-slate-800 rounded-full flex items-center justify-center mb-3">
              <Filter size={24} className="opacity-50" />
            </div>
            No logs match your filters.
          </div>
        )}

        {groupedLogs.map((log, idx) => (
          <div
            key={log.id || idx}
            className={`p-3 rounded-lg border text-sm ${getActionColor(log.action)}`}
          >
            <div className="flex justify-between items-start mb-2">
              <div className="flex items-center gap-2 font-bold uppercase text-[10px] tracking-wider opacity-80">
                {getActionIcon(log.action)}
                {log.action.replace("_", " ")}
                {log.diff && log.diff.length > 1 && (
                  <span className="bg-black/10 dark:bg-white/10 px-1.5 rounded-full text-[9px]">
                    {log.diff.length} updates
                  </span>
                )}
              </div>
              <div className="text-[10px] text-gray-400 font-mono">
                {formatTime(log.timestamp)}
              </div>
            </div>

            <div className="mb-2 text-slate-800 dark:text-slate-200 font-medium">
              <span className="text-gray-500 dark:text-gray-400 font-normal">
                Target:
              </span>{" "}
              {log.target.name || log.target.id}
            </div>

            {log.diff && log.diff.length > 0 && (
              <div className="bg-white/50 dark:bg-black/20 rounded p-2 text-xs space-y-1 mb-2">
                {log.diff.map((d, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2 text-slate-600 dark:text-slate-400 border-b border-black/5 last:border-0 pb-1 last:pb-0"
                  >
                    <span
                      className="font-mono text-[10px] uppercase text-gray-400 w-16 truncate flex-shrink-0"
                      title={d.field}
                    >
                      {d.field}
                    </span>
                    <span className="line-through opacity-50 truncate max-w-[60px]">
                      {String(d.oldValue || "-")}
                    </span>
                    <ArrowRight size={10} className="flex-shrink-0" />
                    <span className="font-bold text-slate-800 dark:text-slate-200 truncate max-w-[100px]">
                      {String(d.newValue)}
                    </span>
                  </div>
                ))}
              </div>
            )}

            <div className="flex items-center gap-2 text-[10px] text-gray-400 border-t border-black/5 dark:border-white/5 pt-2 mt-2">
              <User size={10} />
              <span className="truncate max-w-[200px]">{log.actor.email}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
